// CONSOLA-02 — patrón compartido de guardado visible.
// Regla: el ÉXITO se anuncia sólo después de que el backend confirma la escritura; la vista
// afectada (ficha/listado/contador) se refresca antes del mensaje. Si falla ÚNICAMENTE el
// refresco, se explica que el registro YA quedó guardado y se ofrece actualizar la vista sin
// repetir la escritura. Si la respuesta es INCIERTA (sin respuesta del servidor) no se afirma
// rechazo: se invita a consultar el estado con las lecturas disponibles.
//
// r3: las acciones de recuperación ("Actualizar vista" y "Consultar estado") manejan su promesa
// de forma explícita y siempre dejan un resultado visible. Son LECTURAS: estas funciones no
// reciben `escribir`, así que por construcción no pueden reenviar la escritura, y sus mensajes
// describen el fallo de LECTURA (nunca reutilizan los mensajes de rechazo de escritura).
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

  // r4 (C-7): un refresco puede quedar PARCIAL (una lectura responde y otra no). Si `refrescar`
  // resuelve con { parcial: true, aviso, accionLabel, accion } no se promete una vista completa:
  // se anuncia lo confirmado y se identifica la lectura que no se pudo actualizar, con reintento
  // de ESA lectura. Nunca se reenvía la escritura.
  const parcial = (r) => (r && typeof r === "object" && r.parcial ? r : null);
  const accionDe = (p) => (p.accion ? { label: p.accionLabel || "Reintentar", onClick: () => { p.accion(); } } : undefined);

// Reintento de la LECTURA de refresco tras una escritura ya CONFIRMADA.
function reintentarRefresco(exito, refrescar, res) {
  return Promise.resolve()
    .then(() => refrescar(res))
    .then((r) => {
      const p = parcial(r);
      if (p) {
        toast.warning("La vista se actualizó sólo en parte.", {
          description: p.aviso,
          duration: 15000,
          action: accionDe(p),
        });
        return false;
      }
      toast.success("Vista actualizada.", {
        description: "Ya puedes ver el registro que se había guardado.",
      });
      return true;
    })
    .catch(() => {
      toast.warning("Seguimos sin poder actualizar la vista.", {
        description: "El registro SÍ quedó guardado; lo que falló es la lectura. Puedes intentar actualizar otra vez; no lo captures de nuevo.",
        duration: 15000,
        action: { label: "Actualizar vista", onClick: () => { reintentarRefresco(exito, refrescar, res); } },
      });
      return false;
    });
}

function avisarRefrescoFallido(exito, refrescar, res) {
  toast.warning(`${exito} No pudimos actualizar la vista.`, {
    description: "El registro ya quedó guardado. Actualiza la vista para verlo; no lo captures otra vez.",
    duration: 15000,
    action: { label: "Actualizar vista", onClick: () => { reintentarRefresco(exito, refrescar, res); } },
  });
}

// Consulta del estado tras una escritura INCIERTA. Un GET correcto sólo acredita que se leyó el
// estado: no afirma que la escritura original haya ocurrido.
// Se exporta porque el formulario la ofrece TAMBIÉN en su banner: mientras el diálogo sigue
// abierto (para conservar la captura) su overlay intercepta el clic sobre la acción del aviso.
export function consultarEstadoIncierto(consultarEstado) {
  return Promise.resolve()
    .then(() => consultarEstado())
    .then((r) => {
      const p = parcial(r);
      if (p) {
        toast.warning("El estado se consultó sólo en parte.", {
          description: `${p.aviso} Revisa en pantalla si tu captura ya aparece antes de volver a guardar.`,
          duration: 15000,
          action: accionDe(p),
        });
        return false;
      }
      toast.info("Estado consultado.", {
        description: "Se leyó el estado actual. Revisa en pantalla si tu captura ya aparece; esta lectura no confirma por sí sola que se haya guardado.",
        duration: 15000,
        action: { label: "Consultar otra vez", onClick: () => { consultarEstadoIncierto(consultarEstado); } },
      });
      return true;
    })
    .catch(() => {
      toast.warning("Seguimos sin poder comprobar el estado.", {
        description: "No se pudo leer el estado actual y tu captura sigue en pantalla. Intenta consultar otra vez antes de volver a guardar, para no duplicar el registro.",
        duration: 15000,
        action: { label: "Consultar otra vez", onClick: () => { consultarEstadoIncierto(consultarEstado); } },
      });
      return false;
    });
}

export async function guardarConfirmado({ escribir, refrescar, exito }) {
  const res = await escribir(); // si lanza, lo maneja el llamador (la captura se conserva)
  if (!refrescar) {
    toast.success(exito);
    return { res, refrescoOk: true };
  }
  try {
    const r = await refrescar(res);
    const p = parcial(r);
    if (p) {
      // La escritura SÍ está confirmada; lo que no se pudo actualizar es una de las lecturas.
      toast.warning(`${exito} La vista se actualizó sólo en parte.`, {
        description: p.aviso,
        duration: 15000,
        action: accionDe(p),
      });
      return { res, refrescoOk: false, refrescoParcial: true };
    }
    toast.success(exito);
    return { res, refrescoOk: true };
  } catch {
    avisarRefrescoFallido(exito, refrescar, res);
    return { res, refrescoOk: false };
  }
}

// Traduce el fallo de una escritura a un mensaje que explica permiso, validación o fallo de red.
// `incierto` = no hubo respuesta del servidor: no se puede afirmar que fue rechazado.
export function mensajeErrorGuardado(e) {
  const status = e?.response?.status;
  const detalle = e?.response?.data?.detail;
  if (!e?.response) {
    return {
      incierto: true,
      titulo: "No pudimos confirmar si se guardó",
      detalle: "No hubo respuesta del servidor. Consulta el estado actual antes de volver a guardar para no duplicar el registro.",
    };
  }
  if (status === 401) {
    return { incierto: false, titulo: "Tu sesión expiró", detalle: "Vuelve a iniciar sesión; tu captura sigue en pantalla." };
  }
  if (status === 403) {
    return {
      incierto: false,
      titulo: "No se guardó: tu rol no puede hacer este cambio",
      detalle: formatApiErrorDetail(detalle),
    };
  }
  if (status === 404) {
    return { incierto: false, titulo: "No se guardó: el registro ya no está disponible", detalle: formatApiErrorDetail(detalle) };
  }
  if (status === 422 || status === 400) {
    return { incierto: false, titulo: "No se guardó: revisa los datos capturados", detalle: formatApiErrorDetail(detalle) };
  }
  if (status >= 500) {
    return {
      incierto: true,
      titulo: "No pudimos confirmar si se guardó",
      detalle: "El servidor respondió con un error. Consulta el estado actual antes de volver a guardar.",
    };
  }
  return { incierto: false, titulo: "No se guardó", detalle: formatApiErrorDetail(detalle) };
}

// Muestra el fallo y devuelve el mensaje para pintarlo también dentro del formulario (banner
// persistente: el toast puede pasar desapercibido y el usuario se queda sin explicación).
export function reportarErrorGuardado(e, { consultarEstado } = {}) {
  const m = mensajeErrorGuardado(e);
  const opciones = { description: m.detalle, duration: 12000 };
  if (m.incierto && consultarEstado) {
    opciones.action = { label: "Consultar estado", onClick: () => { consultarEstadoIncierto(consultarEstado); } };
  }
  if (m.incierto) toast.warning(m.titulo, opciones);
  else toast.error(m.titulo, opciones);
  return m;
}
